/**
 * Session lifecycle.
 *
 * Two token types that are not interchangeable:
 *   Clerk's token proves WHO someone is, lives **60 seconds**, and is accepted by exactly two
 *   endpoints. Fig's own token carries WHAT THEY CAN SEE, lives **60 minutes**, and is
 *   accepted by everything else.
 *
 * There is no refresh token, deliberately: re-exchanging re-reads the user row, so disabling
 * somebody takes effect within one token lifetime at worst. The cost of that design is that
 * the client has to refresh AHEAD of expiry rather than discovering it as a 401 - which is why
 * the API returns `expiresInSeconds`.
 *
 * Getting this wrong shows up as a random logout an hour in, which is miserable to diagnose
 * later and reads to the user as the product being unreliable. It is written once, here.
 */

import { FigApiError, parseProblem } from '../api/errors.js';
import type { RoleClaim, SessionResponse } from '../api/types.js';

export interface SessionStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

/** Default for tests and for web contexts that deliberately do not persist a session. */
export class MemorySessionStorage implements SessionStorage {
  #m = new Map<string, string>();
  async get(k: string) { return this.#m.get(k) ?? null; }
  async set(k: string, v: string) { this.#m.set(k, v); }
  async remove(k: string) { this.#m.delete(k); }
}

export type SignOutReason = 'expired' | 'disabled' | 'no-account' | 'signed-out' | 'clerk-lost';

/**
 * The identity provider could not produce a token **right now**.
 *
 * <b>This is not the same as "this person is signed out", and conflating the two is the classic
 * way a mobile app signs somebody out in a lift.</b> Clerk's `getToken()` returns `null` for
 * several reasons that have nothing to do with the account: the SDK has not finished loading, its
 * own 60-second token expired and the refresh call could not reach Clerk, or Clerk itself is
 * having a bad minute. Every one of those is over in seconds.
 *
 * Until 2026-09-20 a null here destroyed the stored session and threw a 401, which the caller's
 * own transient/permanent test then read as a definite answer about the identity — so the retry
 * loop broke out immediately and the person landed on the sign-in screen with nothing on screen
 * explaining why. Worse, it was a one-way door: the session was already erased by the time the
 * network came back, and nothing re-ran until the app was restarted.
 *
 * <b>Who is actually allowed to end a session:</b> the identity provider, by reporting signed-out
 * (the host app watches that and calls {@link FigSession.signOut}), or the API, by answering 401,
 * 403 or 404 to a real exchange. Not a missing token.
 */
export class ClerkTokenUnavailableError extends Error {
  constructor() {
    super('The identity provider could not issue a token right now. This is usually transient.');
    this.name = 'ClerkTokenUnavailableError';
  }
}

/**
 * Which door this session came through, and therefore which endpoint renews it.
 *
 * `clerk` — a real account. `demo` — the read-only "have a look around" tour, which has no
 * identity at all and is renewed by asking for another tour token rather than by presenting a
 * Clerk token there is no such thing as.
 *
 * **This is persisted**, because the renewal path has to survive a cold start. A restored demo
 * session that tried to renew through Clerk would sign the visitor out mid-tour, an hour in, for
 * reasons nobody could see.
 */
export type SessionKind = 'clerk' | 'demo';

export interface SessionOptions {
  readonly apiBaseUrl: string;
  /**
   * Return a FRESH Clerk token. Called immediately before every exchange, never cached by us:
   * Clerk's own lifetime is 60 seconds, so a token held even briefly is usually already dead.
   */
  readonly getClerkToken: () => Promise<string | null>;
  readonly storage?: SessionStorage;
  /** Refresh this far ahead of expiry. Default 5 minutes of a 60-minute token. */
  readonly refreshSkewSeconds?: number;
  readonly onSignedOut?: (reason: SignOutReason) => void;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
}

interface StoredSession {
  readonly token: string;
  readonly role: RoleClaim;
  readonly expiresAtMs: number;
  /** The `user_id` claim, so a client can tell which row in a list is the person using it. */
  readonly userId: string | null;
  /**
   * Whether this person may switch environments. Comes from the API on every exchange rather
   * than from the token, so revoking it takes effect within one token lifetime.
   *
   * Persisted with the rest of the session only so a cold start does not render the app without
   * it and then flicker it in. It is re-read and overwritten on the very next exchange, so a
   * stale `true` in storage survives at most one token lifetime — and the switcher it controls
   * is inert anyway unless the environment it would switch to is in the client's own hardcoded
   * allowlist.
   */
  readonly isInternal: boolean;
  /**
   * When this person's account is due to be deleted, ISO-8601, or absent when none is scheduled.
   *
   * **Carried on the session because it decides which screen the app opens.** Somebody who has
   * asked to delete their account must land on the restore screen rather than on their calls —
   * seeing the product carry on as though nothing happened is how a person concludes the deletion
   * did not work.
   *
   * Signing in is deliberately still permitted while this is set: the recovery window is the whole
   * point of scheduling, and somebody who cannot get in cannot change their mind. The API refuses
   * everything EXCEPT the restore path; this field is what lets the app say why.
   */
  readonly deletionDueAt?: string;
  /**
   * Absent on sessions written before the tour existed. Read through {@link FigSession.kind},
   * which defaults it to `'clerk'` — the state that behaves exactly as it always has.
   */
  readonly kind?: SessionKind;
}

/**
 * Read a claim out of Fig's own token.
 *
 * No signature check, deliberately: this is our token, already validated by the API on every
 * call, and a client verifying it would be theatre. It is read only to answer questions the
 * UI needs — "which of these users am I" — never to decide anything a server also decides.
 */
function readClaim(jwt: string, claim: string): string | null {
  try {
    const part = jwt.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    const value = payload[claim];
    return typeof value === 'string' ? value : null;
  } catch { return null; }
}

const KEY = 'fig.session.v1';

/**
 * How much life a token must have left before it is worth handing to a request whose refresh just
 * failed. Ten seconds: long enough to cover a slow request on a bad connection, short enough that
 * nothing is ever handed a token that expires mid-flight.
 */
const UsableRemainderMs = 10_000;

export interface SignupRequest {
  readonly agencyName: string;
  readonly userName: string;
  readonly mode: 'Agency' | 'Direct';
  /**
   * IANA zone, read from the device. **Never ask for it** - the device holds the answer, and
   * asking spends a person's patience for information we already have. Omitting it means UTC,
   * and a UTC agency in Melbourne resolves every calendar-day filter ten hours out with no
   * error anywhere to notice. `PATCH /api/agency` is the correction path, in Settings.
   */
  readonly timeZoneId?: string;
}

/** `Intl.DateTimeFormat().resolvedOptions().timeZone` - works on web and React Native. */
export function detectTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.length > 0 ? tz : undefined;
  } catch { return undefined; }
}

export class FigSession {
  #opts: Required<Pick<SessionOptions, 'refreshSkewSeconds'>> & SessionOptions;
  #storage: SessionStorage;
  #fetch: typeof fetch;
  #now: () => number;
  #current: StoredSession | null = null;
  /** Single-flight: twenty concurrent screens must produce ONE exchange, not twenty. */
  #inflight: Promise<StoredSession> | null = null;
  #loaded = false;

  constructor(opts: SessionOptions) {
    this.#opts = { refreshSkewSeconds: 300, ...opts };
    this.#storage = opts.storage ?? new MemorySessionStorage();
    this.#fetch = opts.fetchImpl ?? globalThis.fetch;
    this.#now = opts.now ?? (() => Date.now());
  }

  get role(): RoleClaim | null { return this.#current?.role ?? null; }
  /**
   * The signed-in person's Fig user id. Needed for the rules the API enforces that a UI should
   * not make someone discover as an error - "you cannot disable yourself" being the live one.
   */
  get userId(): string | null { return this.#current?.userId ?? null; }
  get expiresAtMs(): number | null { return this.#current?.expiresAtMs ?? null; }
  get isSignedIn(): boolean { return this.#current !== null; }
  /**
   * Whether this person may switch the app between environments.
   *
   * Defaults to false when there is no session at all, which is the state a sign-in screen is
   * in — so the switcher can never appear before somebody has proved who they are.
   */
  get isInternal(): boolean { return this.#current?.isInternal === true; }

  /**
   * The date this account is deleted, or null. Read by the router to decide whether to show the
   * restore screen instead of the app.
   */
  get deletionDueAt(): string | null { return this.#current?.deletionDueAt ?? null; }

  /** `'clerk'` for a real account, `'demo'` for the tour. No session is treated as `'clerk'`. */
  get kind(): SessionKind { return this.#current?.kind ?? 'clerk'; }

  /**
   * True only while a tour is actually in progress. Every surface that has to say "this is sample
   * data" reads this one property — the band, the read-only refusals, the way out.
   */
  get isDemo(): boolean { return this.#current?.kind === 'demo'; }

  /** Restore a session persisted by a previous run. Safe to call more than once. */
  async restore(): Promise<boolean> {
    if (this.#loaded) return this.#current !== null;
    this.#loaded = true;
    const raw = await this.#storage.get(KEY);
    if (!raw) return false;
    try {
      const s = JSON.parse(raw) as StoredSession;
      if (typeof s?.token === 'string' && typeof s?.expiresAtMs === 'number') {
        this.#current = s;
        return true;
      }
    } catch { /* corrupt entry is the same as no entry */ }
    await this.#storage.remove(KEY);
    return false;
  }

  /**
   * A token that is valid now and will still be valid for the length of the request.
   * Refreshes proactively inside the skew window; never returns an expired token.
   */
  async getToken(): Promise<string> {
    await this.restore();
    const s = this.#current;
    if (s && s.expiresAtMs - this.#now() > this.#opts.refreshSkewSeconds * 1000) return s.token;

    try {
      return (await this.#exchangeOnce()).token;
    } catch (e) {
      /*
       * **The early refresh failed, but the token in hand may still be good.**
       *
       * The skew window exists so a refresh happens with five minutes to spare. That spare time is
       * the whole point: if the refresh cannot complete — no signal, Clerk unreachable, the API
       * cold-starting — the right answer is to keep using the token we already hold and try again
       * on the next call, not to fail the request the person is waiting on.
       *
       * Throwing here instead meant a flicker of bad connectivity at minute 56 of a 60-minute
       * token broke the app for someone whose credentials were perfectly valid.
       *
       * **Only while it is genuinely unexpired**, with a few seconds' headroom so it cannot die in
       * flight, and only when the session still exists — if the exchange cleared it (a real 401,
       * 403 or 404 about this identity), `#current` is null and the failure propagates, which is
       * correct.
       */
      const still = this.#current;
      if (still && still.expiresAtMs - this.#now() > UsableRemainderMs) return still.token;
      throw e;
    }
  }

  /** Force a re-exchange - what the client calls after a 401, exactly once. */
  async forceRefresh(): Promise<string> {
    return (await this.#exchangeOnce()).token;
  }

  async #exchangeOnce(): Promise<StoredSession> {
    if (this.#inflight) return this.#inflight;
    this.#inflight = this.#exchange().finally(() => { this.#inflight = null; });
    return this.#inflight;
  }

  /**
   * Start the read-only tour: a session with no identity, on whichever tenant the API has been
   * configured to hand out.
   *
   * **Throws `FigApiError(404)` when this environment offers no demo**, which is the normal answer
   * and not an error condition — the caller uses it to decide whether to show the button at all,
   * so there is exactly one source of truth (the API) rather than a client-side flag that can drift
   * out of step with what is actually seeded.
   */
  async startDemo(): Promise<StoredSession> {
    // Not through #exchangeOnce: this deliberately replaces whatever session exists rather than
    // joining an in-flight refresh of a different one.
    const stored = await this.#exchangeDemo();
    this.#loaded = true;
    return stored;
  }

  async #exchangeDemo(): Promise<StoredSession> {
    const res = await this.#fetch(`${this.#opts.apiBaseUrl}/api/auth/demo`, { method: 'POST' });
    if (!res.ok) throw await toError(res);

    const body = (await res.json()) as SessionResponse;
    const stored: StoredSession = {
      token: body.token,
      role: body.role,
      expiresAtMs: this.#now() + body.expiresInSeconds * 1000,
      userId: null,
      // A tour is never internal and never switches environments, whatever the body says.
      isInternal: false,
      kind: 'demo',
    };
    this.#current = stored;
    await this.#storage.set(KEY, JSON.stringify(stored));
    return stored;
  }

  async #exchange(): Promise<StoredSession> {
    // A tour renews by asking for another tour. Reaching the Clerk path with a demo session would
    // find no Clerk token and sign the visitor out an hour in - which is the single most
    // confusing thing this feature could do, because nothing on screen would explain it.
    if (this.#current?.kind === 'demo') return this.#exchangeDemo();

    // NOT a sign-out. See ClerkTokenUnavailableError: a missing token is a statement about this
    // moment, and the session stays exactly as it was so the next attempt can still use it.
    const clerk = await this.#opts.getClerkToken();
    if (!clerk) throw new ClerkTokenUnavailableError();

    const res = await this.#post('/api/auth/session', clerk, undefined);
    if (!res.ok) {
      // 404: this Clerk identity has no Fig account. 403: disabled, at the user or the client
      // level. Neither is recoverable by retrying, so both end the session rather than looping.
      if (res.status === 404) { await this.#clear('no-account'); }
      else if (res.status === 403) { await this.#clear('disabled'); }
      else if (res.status === 401) { await this.#clear('expired'); }
      throw await toError(res);
    }
    const body = (await res.json()) as SessionResponse;
    const stored: StoredSession = {
      token: body.token,
      role: body.role,
      expiresAtMs: this.#now() + body.expiresInSeconds * 1000,
      userId: readClaim(body.token, 'user_id'),
      kind: 'clerk',
      // `=== true` rather than a truthy check: undefined (an older API, or a Client Portal
      // response) and any non-boolean the wire might carry both resolve to false. The safe
      // answer is the one you get by not being told.
      isInternal: body.isInternal === true,
      // Only stored when present, so an older API response or a Client Portal one leaves it
      // absent rather than recording a deletion nobody asked for.
      ...(typeof body.deletionDueAt === 'string' ? { deletionDueAt: body.deletionDueAt } : {}),
    };
    this.#current = stored;
    await this.#storage.set(KEY, JSON.stringify(stored));
    return stored;
  }

  /** Create a brand-new agency and its first (always Admin) user. 409 if one already exists. */
  async signup(req: SignupRequest): Promise<StoredSession> {
    const clerk = await this.#opts.getClerkToken();
    if (!clerk) throw new ClerkTokenUnavailableError();
    const body = { ...req, timeZoneId: req.timeZoneId ?? detectTimeZone() };
    const res = await this.#post('/api/auth/signup', clerk, body);
    if (!res.ok) throw await toError(res);
    // Signup returns the created account; the session token still comes from the exchange.
    await res.text();
    return this.#exchangeOnce();
  }

  async signOut(): Promise<void> { await this.#clear('signed-out'); }

  async #clear(reason: SignOutReason): Promise<void> {
    this.#current = null;
    await this.#storage.remove(KEY);
    this.#opts.onSignedOut?.(reason);
  }

  #post(path: string, clerkToken: string, body: unknown): Promise<Response> {
    return this.#fetch(`${this.#opts.apiBaseUrl}${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${clerkToken}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  }
}

async function toError(res: Response): Promise<FigApiError> {
  const text = await res.text().catch(() => '');
  return new FigApiError(res.status, parseProblem(text), text || null);
}
